/**
 * The Inbox's side panel (inbox.md 4.3.2): the selected item in full, its exact call, the facts
 * that decide it and one decision bar. Docked beside the list on a wide screen, a drawer below.
 * Each kind renders its parts through `frame`, so the docked panel and the drawer share them.
 */
import { Ban, Check, ChevronDown, Copy, Ellipsis, FileCode2, Link2, MessagesSquare, RotateCcw, ScrollText, ShieldCheck, SquareTerminal } from '@orgus/ui/icons';
import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { TIER_LABEL, askIdOf, approvalResumable, delegatedNote, delegatedPolicyText, sessionReason } from '@orgus/protocol';
import type { AgentKind } from '@orgus/protocol';
import { AskActions, HandBackButton, SetAsideLine } from '../asks/AskActions.tsx';
import { askViewOf } from '../session-detail/asks.ts';
import type { ApprovalRequest } from '@orgus/protocol';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Input,
  Markdown,
  Pressable,
  ProjectMark,
  StatusPill,
  Tooltip,
  cn,
  formatAbsolute,
  formatClock,
  formatDuration,
  toast,
} from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';
import { plainText } from '../../lib/plainText.ts';
import { useOrgus } from '../../lib/store.ts';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { useStepAnswer, tierBlockedText } from '../assistant/ApprovalStep.tsx';
import { stepInputJson } from '../assistant/model.ts';
import { ApproveAndResumeButton, ResumeError, useApproveAndResume } from './ApproveAndResumeButton.tsx';
import { MarkHandledButton } from './ApprovalBlocker.tsx';
import { usePersonName } from '../missions/people.ts';
import { blockView, commandOf, grantUntil, useMeId } from './blocked.ts';
import { useApprovals } from './live.ts';
import { ApprovalAnswer, useDecision } from './ApprovalAnswer.tsx';
import { AskAnswer, AskRecord, TimeLeft, askHeading, useAnswerer } from './AskAnswer.tsx';
import { answerLine, askOf, askSummary, decidedText, fallbackLine, givenOf, isAnswerable, planTitle, questionAnswersOf } from './askModel.ts';
import { blockedCause, doneLabel, shortTarget, shortTitle, titleParts } from './inboxModel.ts';
import { AgentGlyph } from '../adapters/AgentKind.tsx';
import { FACT_CELL, FACT_GRID } from '../sessions/SessionFacts.tsx';
import { RiskLabel } from './RiskLabel.tsx';
import type { InboxItem } from './inboxModel.ts';

/** The parts of one panel: the docked aside and the drawer lay them out the same way. */
export interface DetailParts {
  status: ReactNode;
  /** The plain title: the panel's accessible name. */
  title: string;
  /** The title as shown, when it is richer than words (a verb and a path in mono). */
  heading?: ReactNode;
  context?: ReactNode;
  /** The labelled facts under the title (FactGrid). */
  facts?: ReactNode;
  tools?: ReactNode;
  body: ReactNode;
  footer?: ReactNode;
}

export type Frame = (parts: DetailParts) => ReactNode;

export interface DetailContext {
  session?: string;
  /** The session's agent, for its mark beside the session. */
  agentKind?: AgentKind | null;
  mission?: string;
  project?: string;
  projectId?: string | null;
  machine?: string;
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

interface Fact {
  label: string;
  value: ReactNode;
  /** The whole value, when the cell cuts it short. */
  title?: string;
  mono?: boolean;
  /** A long value that must read whole (a path): a full-width cell under its line, wrapped, never cut. */
  wide?: boolean;
}

/**
 * The labelled facts of an item, in the session drawer's grid (FACT_GRID): a tiny dim label over
 * each value, cells set apart by thin inset lines. Each inner list is one line of up to four
 * cells; a line of two spans the width in halves, so its labels sit over the first and third
 * columns of the line below. Facts without a value do not render.
 */
function FactGrid({ rows }: { rows: Fact[][] }) {
  const lines = rows.map((r) => r.filter((f) => f.value != null && f.value !== '')).filter((r) => r.length);
  if (!lines.length) return null;
  return (
    <div className="flex flex-col gap-4">
      {lines.map((line, i) => {
        const cells = line.filter((f) => !f.wide);
        // A line of three keeps one row on a phone too (three short values), never two and a lone cell.
        const three = cells.length === 3;
        return (
          <Fragment key={i}>
            {cells.length > 0 && (
              <dl className={cn(FACT_GRID, cells.length <= 2 && 'sm:grid-cols-2', three && 'max-sm:grid-cols-3')}>
                {cells.map((f, j) => (
                  <div key={f.label} className={cn(FACT_CELL, 'gap-1', three && j > 0 && 'max-sm:pl-4', three && j === 2 && 'max-sm:before:block!')}>
                    <dt className="truncate text-caption text-fg-subtle">{f.label}</dt>
                    {/* Three to a phone row: a value wraps (at a hyphen or space) rather than cut. */}
                    <dd className={cn('flex h-5 min-w-0 items-center gap-1 text-ui text-fg-muted', f.mono && 'font-mono', three && 'max-sm:h-auto max-sm:items-start max-sm:[&_span]:whitespace-normal')} title={f.title}>
                      {typeof f.value === 'string' ? <span className="min-w-0 truncate">{f.value}</span> : f.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {line
              .filter((f) => f.wide)
              .map((f) => (
                <dl key={f.label} className="flex min-w-0 flex-col gap-1">
                  <dt className="text-caption text-fg-subtle">{f.label}</dt>
                  <dd className={cn('min-w-0 break-all text-ui text-fg-muted', f.mono && 'font-mono')}>{f.value}</dd>
                </dl>
              ))}
          </Fragment>
        );
      })}
    </div>
  );
}

/** The session after its agent's mark, for a fact cell. */
function SessionValue({ kind, name }: { kind: AgentKind | null | undefined; name: string }) {
  return (
    <>
      <AgentGlyph kind={kind} className="size-3 shrink-0" />
      <span className="min-w-0 truncate">{name}</span>
    </>
  );
}

/** The project after its mark, for a fact cell. */
function ProjectValue({ projectId, name }: { projectId: string | null | undefined; name: string }) {
  return (
    <>
      {projectId && <ProjectMark projectId={projectId} name={name} size="xs" />}
      <span className="min-w-0 truncate">{name}</span>
    </>
  );
}

/** Where a request comes from: its session and its project, the first line of its facts. */
function whereFacts(c: DetailContext): Fact[] {
  const session = c.mission ?? c.session;
  return [
    { label: c.mission ? 'Mission' : 'Session', value: session ? <SessionValue kind={c.agentKind} name={session} /> : undefined, title: session },
    { label: 'Project', value: c.project ? <ProjectValue projectId={c.projectId} name={c.project} /> : undefined, title: c.project },
  ];
}

/** A short calm passage with a tiny label: why a request asks, what the agent said. */
function Note({ label, children }: { label: string; children: ReactNode }) {
  if (children == null || children === '') return null;
  return (
    <section className="flex flex-col gap-1">
      <h3 className="text-caption text-fg-subtle">{label}</h3>
      <p className="text-ui text-fg-muted">{children}</p>
    </section>
  );
}

/** A request's title as the panel shows it: the verb in words, the target in mono, cut from the middle, whole in the tooltip. */
function RequestHeading({ a }: { a: Pick<ApprovalRequest, 'title' | 'input'> }) {
  const p = titleParts(a);
  if (!p.target) return <>{p.verb}</>;
  const short = shortTarget(p, 48);
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="shrink-0">{p.verb}</span>
      <Tooltip content={<span className="font-mono wrap-anywhere">{p.full}</span>} enabled={short !== p.full}>
        <span tabIndex={short !== p.full ? 0 : undefined} className="min-w-0 rounded-control font-mono text-ui font-normal text-fg-muted wrap-anywhere">
          {short}
        </span>
      </Tooltip>
    </span>
  );
}

const clock = (at: number) => (
  <Tooltip content={formatAbsolute(at)}>
    <span tabIndex={0} className="num">
      {formatClock(at)}
    </span>
  </Tooltip>
);

const rec = (x: unknown): Record<string, unknown> => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Record<string, unknown>) : {});
const str = (x: unknown): string | undefined => (typeof x === 'string' && x ? x : undefined);

/** The exact call on the well: a shell command, an edit as removed and added lines, or the input. */
export function CommandBlock({ approval, compact }: { approval: Pick<ApprovalRequest, 'tool' | 'input' | 'subject' | 'title'>; compact?: boolean }) {
  const input = rec(approval.input);
  const tool = (approval.tool ?? '').toLowerCase();
  const well = cn('overflow-auto rounded-card bg-sunken font-mono leading-relaxed text-fg whitespace-pre-wrap break-all', compact ? 'max-h-32 px-3 py-2 text-caption' : 'max-h-72 px-4 py-3 text-ui');
  const copy = commandOf(approval as ApprovalRequest) ?? approval.title;
  let body: ReactNode;
  if ((tool === 'bash' || tool === 'powershell') && str(input.command)) {
    body = (
      <pre className={well} aria-label="Command">
        <span className="select-none text-fg-subtle">$ </span>
        {str(input.command)}
      </pre>
    );
  } else if ((tool === 'edit' || tool === 'multiedit') && (str(input.old_string) != null || Array.isArray(input.edits))) {
    const edits = Array.isArray(input.edits) ? (input.edits as Record<string, unknown>[]) : [input];
    body = (
      <div className="flex flex-col gap-2">
        <p className="flex min-w-0 items-start gap-2 font-mono text-caption text-fg-subtle">
          <span className="flex h-4 shrink-0 items-center" aria-hidden>
            <FileCode2 className="size-3" />
          </span>
          <span className="min-w-0 wrap-anywhere">{str(input.file_path) ?? approval.subject}</span>
        </p>
        <div className={cn(well, 'px-0')} role="group" aria-label="Change">
          {edits.slice(0, 3).map((e, i) => (
            <div key={i} className="flex flex-col">
              {String(e.old_string ?? '')
                .split('\n')
                .slice(0, 8)
                .map((l, j) => (
                  <span key={`o${j}`} className="bg-status-danger-soft/50 px-4 text-fg-muted">
                    <span className="select-none text-fg-subtle">- </span>
                    {l}
                  </span>
                ))}
              {String(e.new_string ?? '')
                .split('\n')
                .slice(0, 8)
                .map((l, j) => (
                  <span key={`n${j}`} className="bg-status-success-soft/50 px-4 text-fg">
                    <span className="select-none text-fg-subtle">+ </span>
                    {l}
                  </span>
                ))}
            </div>
          ))}
        </div>
      </div>
    );
  } else if (approval.subject && Object.keys(input).length <= 2) {
    body = <pre className={well}>{approval.subject}</pre>;
  } else {
    body = <pre className={well}>{JSON.stringify(approval.input ?? {}, null, 2)}</pre>;
  }
  return (
    <div className="group/cmd relative">
      {body}
      <span className="absolute right-2 top-2 opacity-0 transition-opacity duration-(--duration-fast) focus-within:opacity-100 group-hover/cmd:opacity-100">
        <IconButton
          size="xs"
          label="Copy the command"
          icon={<Copy />}
          onClick={() => {
            void navigator.clipboard?.writeText(copy);
            toast('Copied the command', { tone: 'neutral' });
          }}
        />
      </span>
    </div>
  );
}

function Tools({ sessionId, link }: { sessionId?: string; link: string }) {
  const [, openSession] = useSessionParam();
  return (
    <>
      {sessionId && <IconButton size="sm" label="Open session" icon={<SquareTerminal />} onClick={() => openSession(sessionId)} />}
      <IconButton
        size="sm"
        label="Copy link"
        icon={<Link2 />}
        onClick={() => {
          void navigator.clipboard?.writeText(new URL(link, window.location.origin).toString());
          toast('Copied the link', { tone: 'neutral' });
        }}
      />
    </>
  );
}

function ErrorLine({ text }: { text?: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="rounded-control bg-status-danger-soft px-3 py-2 text-ui text-fg">
      {text}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

export interface RequestHandlers {
  /**
   * The open request's answer keys (a, s, r, d, Esc), handed to the page while this request is
   * the one on screen; null when it leaves. The page calls it only while the panel is visible.
   */
  bindKeys: (fn: ((e: KeyboardEvent) => boolean) | null) => void;
  /** Show each button's key: only where the keys work. */
  keys: boolean;
}

/** What a risk level means when you answer (the same rule on every surface, decision.ts). */
const RISK_TEXT: Record<ApprovalRequest['risk'], string> = { low: 'Low risk.', medium: 'Medium risk.', high: 'High risk: allowing it asks you to confirm.' };

/**
 * A request's risk, said once in the header: the row's own label (grey for low and medium, danger
 * only for high), with what it means in the tooltip. Never a second filled pill beside Needs you.
 */
function HeaderRisk({ risk }: { risk: ApprovalRequest['risk'] }) {
  return (
    <Tooltip content={RISK_TEXT[risk]}>
      <span tabIndex={0} className="flex rounded-control">
        <RiskLabel risk={risk} />
      </span>
    </Tooltip>
  );
}

/**
 * How long is left, in the header beside the status, the way the question detail and the answer
 * sheet say it ("2m left to answer"); what happens then is its tooltip. No bare meter beside it: a
 * line with no label read as noise (R-FN-8).
 */
function HeaderTimeLeft({ a, now }: { a: Pick<ApprovalRequest, 'createdAt' | 'expiresAt' | 'fallback'>; now: number }) {
  const left = a.expiresAt - now;
  const urgent = left < 3 * 60_000;
  // Future tense: it has not happened yet.
  const then = a.fallback === 'session' ? 'Then Claude asks in the session.' : `Denies at ${formatClock(a.expiresAt)} if no one answers.`;
  return (
    <span className='flex items-center gap-2'>
      <Tooltip content={`Asked at ${formatClock(a.createdAt)}. ${then}`}>
        <span tabIndex={0} className={cn('shrink-0 rounded-control text-caption num', urgent ? 'font-medium text-status-attention' : 'text-fg-subtle')}>
          {left > 0 ? `${formatDuration(left).split(' ')[0]} left to answer` : 'Expiring'}
        </span>
      </Tooltip>
    </span>
  );
}

function RequestDetail({ a, ctx, now, h, frame }: { a: ApprovalRequest; ctx: DetailContext; now: number; h: RequestHandlers; frame: Frame }) {
  const [, openSession] = useSessionParam();
  const c = useDecision(a, { machine: ctx.machine, project: ctx.project, files: true });
  const { bindKeys } = h;
  useEffect(() => {
    bindKeys(c.onKey);
    return () => bindKeys(null);
  }, [bindKeys, c.onKey]);
  const input = rec(a.input);
  const reason = a.reason ?? str(input.description);

  const footer = (
    <>
      <ApprovalAnswer
        c={c}
        layout="bar"
        keys={h.keys}
        menu={
          <DropdownMenuItem icon={<SquareTerminal />} onSelect={() => openSession(a.sessionId)}>
            Open session
          </DropdownMenuItem>
        }
      />
      {c.dialog}
    </>
  );

  return frame({
    status: (
      <>
        <StatusPill status="needs-you" />
        <HeaderRisk risk={a.risk} />
        <HeaderTimeLeft a={a} now={now} />
      </>
    ),
    title: a.title,
    heading: <RequestHeading a={a} />,
    facts: (
      <FactGrid
        rows={[
          whereFacts(ctx),
          [
            { label: 'Machine', value: ctx.machine },
            { label: 'Tool', value: a.tool, mono: true },
            { label: 'Asked', value: clock(a.createdAt) },
          ],
        ]}
      />
    ),
    tools: <Tools sessionId={a.sessionId} link={`/inbox?approval=${encodeURIComponent(a.id)}`} />,
    body: (
      <div className="flex flex-col gap-6">
        <CommandBlock approval={a} />
        <Note label="Why it asks">{a.policyHint?.reason ?? (a.policyHint?.decision === 'ask' ? 'Policy asks a person for this.' : undefined)}</Note>
        <Note label="The agent's reason">{reason}</Note>
        {a.fallback === 'session' && <HandBackButton approvalId={a.id} />}
      </div>
    ),
    footer,
  });
}

// ---------------------------------------------------------------------------
// Blocked
// ---------------------------------------------------------------------------

function BlockedDetail({ item, ctx, frame }: { item: Extract<InboxItem, { kind: 'blocked' }>; ctx: DetailContext; frame: Frame }) {
  const { approval: a, block } = item;
  const navigate = useNavigate();
  const [, openSession] = useSessionParam();
  const { approvals } = useApprovals();
  const sessionStatus = useOrgus((s) => s.sessions[a.sessionId]?.status);
  const me = useMeId();
  const name = usePersonName();
  const v = blockView(block, { machineName: ctx.machine ?? 'its machine', grantUntil: grantUntil(approvals, a), sessionStatus, me, name });
  // A redirect leads with Mark as handled; Approve and resume waits in the menu behind its confirm.
  const handled = v.lead === 'handled';
  const resume = useApproveAndResume(handled ? a.id : null, { alwaysConfirm: true, redirect: v.redirect });
  const status: PillStatus = v.kind === 'resuming' ? 'working' : v.kind === 'resume_failed' || v.kind === 'not_run' ? 'failed' : 'needs-you';
  const reason = v.kind === 'resuming' || v.kind === 'resume_failed' || v.kind === 'not_run' || v.kind === 'live' ? v.label : `Blocked: ${blockedCause(a)}`;
  return frame({
    status: (
      <>
        <StatusPill status={status} label={status === 'working' ? 'Resuming' : undefined} />
        {a.risk === 'high' && <HeaderRisk risk="high" />}
      </>
    ),
    title: ctx.mission ?? ctx.session ?? a.title,
    heading: (
      // Two lines before it is cut: on a phone one line held only a few words of the title.
      <span className="flex min-w-0 items-start gap-2">
        <AgentGlyph kind={ctx.agentKind} className="mt-1 size-4 shrink-0" />
        <span className="line-clamp-2 min-w-0">{ctx.mission ?? ctx.session ?? a.title}</span>
      </span>
    ),
    facts: (
      <FactGrid
        rows={[
          // The command is the well below, so it is not a fact as well.
          [
            { label: 'Project', value: ctx.project ? <ProjectValue projectId={ctx.projectId} name={ctx.project} /> : undefined, title: ctx.project },
            { label: 'Machine', value: ctx.machine },
          ],
          [
            { label: 'Tool', value: a.tool, mono: true },
            { label: 'Asked', value: clock(a.createdAt) },
            { label: 'Decided', value: a.decidedAt ? clock(a.decidedAt) : undefined },
          ],
        ]}
      />
    ),
    tools: <Tools sessionId={a.sessionId} link={`/inbox?approval=${encodeURIComponent(a.id)}`} />,
    body: (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <p className="text-ui font-medium text-fg">{reason}</p>
          {v.note && <p className="rounded-card bg-hover px-4 py-3 text-ui text-fg">{a.note ?? v.note}</p>}
          {v.detail && <p className="text-ui text-fg-muted">{v.detail}</p>}
          {v.why && <p className="text-caption text-fg-subtle">{v.why}</p>}
        </div>
        <CommandBlock approval={a} />
        <Note label="Rule">{v.ruleId ? <span className="font-mono text-caption">{v.ruleId}</span> : undefined}</Note>
      </div>
    ),
    footer:
      v.kind === 'resuming' ? (
        <div className="flex w-full items-center justify-end gap-2">
          <Button size="sm" variant="secondary" icon={<SquareTerminal />} onClick={() => openSession(a.sessionId)}>
            Open session
          </Button>
        </div>
      ) : (
        // On a phone the header's terminal tool opens the session, so the footer keeps one row: more, handled, the primary.
        <div className="flex w-full flex-wrap items-start justify-end gap-2">
          {(a.missionId || handled) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton size="sm" label="More actions" icon={<Ellipsis />} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {handled && (
                  <DropdownMenuItem icon={<ShieldCheck />} disabled={!!resume.reason || resume.busy} onSelect={resume.start}>
                    Approve and resume…
                  </DropdownMenuItem>
                )}
                {a.missionId && (
                  <DropdownMenuItem icon={<RotateCcw />} onSelect={() => navigate(`/missions/${encodeURIComponent(a.missionId!)}?retry=1`)}>
                    Retry mission
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!handled && <MarkHandledButton approvalId={a.id} />}
          <span className="mr-auto" />
          {resume.answer}
          {resume.error && <ResumeError error={resume.error} onRetry={resume.retry} />}
          {resume.dialog}
          {handled ? (
            <>
              <Button size="sm" variant="secondary" icon={<SquareTerminal />} className="max-sm:hidden" onClick={() => openSession(a.sessionId)}>
                Open session
              </Button>
              <MarkHandledButton approvalId={a.id} variant="primary" />
            </>
          ) : v.kind === 'policy' ? (
            <>
              <Button size="sm" variant="secondary" icon={<SquareTerminal />} className="max-sm:hidden" onClick={() => openSession(a.sessionId)}>
                Open session
              </Button>
              <Button size="sm" variant="primary" icon={<ScrollText />} onClick={() => navigate('/settings/policies')}>
                Open the rule
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="secondary" icon={<SquareTerminal />} className="max-sm:hidden" onClick={() => openSession(a.sessionId)}>
                Open session
              </Button>
              {v.resumable && <ApproveAndResumeButton approvalId={a.id} />}
            </>
          )}
        </div>
      ),
  });
}

// ---------------------------------------------------------------------------
// Assistant step
// ---------------------------------------------------------------------------

/** "dryRun" and "dry_run" as "Dry run": a step input's key as a fact label. */
const keyLabel = (k: string): string => {
  const words = k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A step input's plain values (text, numbers, yes or no) as labelled facts, at most four. */
function inputFacts(input: Readonly<Record<string, unknown>>): Fact[] {
  return Object.entries(input)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
    .slice(0, 4)
    .map(([k, v]) => {
      const mono = typeof v === 'string' && /[/\\]/.test(v);
      return { label: keyLabel(k), value: typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v), title: String(v), mono, wide: mono && String(v).length > 16 };
    });
}

/** Why a step asks, said plainly: the autonomy setting's rule reads as what the tier needs. */
function stepWhy(step: { tier: keyof typeof TIER_LABEL; approval?: { why?: string | null } | null }): string {
  const why = step.approval?.why;
  const tier = TIER_LABEL[step.tier];
  if (!why || why.endsWith(` asks before ${tier.toLowerCase()} changes.`)) return `${tier} changes need your approval.`;
  return why;
}

function StepDetail({ item, frame, onDone }: { item: Extract<InboxItem, { kind: 'step' }>; frame: Frame; onDone: () => void }) {
  const p = item.step;
  const s = useStepAnswer(p.conversationId, p.step);
  const navigate = useNavigate();
  const [raw, setRaw] = useState(false);
  const facts = inputFacts(p.step.input);
  const answer = async (x: 'allow' | 'deny') => {
    if (await s.answer(x)) {
      toast(x === 'allow' ? `Approved: ${p.step.title}` : `Denied: ${p.step.title}`, { tone: x === 'allow' ? 'success' : 'neutral' });
      onDone();
    }
  };
  return frame({
    status: (
      <>
        <StatusPill status="needs-you" />
        {p.step.tier === 'critical' && <HeaderRisk risk="high" />}
      </>
    ),
    title: p.step.title,
    facts: (
      <FactGrid
        rows={[
          // The capability is what "Why it asks" says, so it is not a fact as well. What the step
          // will do joins the same grid: no second block of facts below a gap.
          [
            { label: 'Conversation', value: p.conversationTitle, title: p.conversationTitle },
            { label: 'Asked', value: clock(p.at) },
          ],
          facts,
        ]}
      />
    ),
    tools: <IconButton size="sm" label="Open conversation" icon={<MessagesSquare />} onClick={() => navigate(`/assistant?c=${encodeURIComponent(p.conversationId)}`)} />,
    body: (
      <div className="flex flex-col gap-6">
        {/* What the step will do, as labelled facts; the exact input is one quiet step away. */}
        <div className="flex flex-col gap-3">
          <Pressable
            onClick={() => setRaw((x) => !x)}
            aria-expanded={raw}
            className="flex w-auto items-center gap-1 self-start rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
          >
            {raw ? 'Hide the exact input' : 'Show the exact input'}
            <ChevronDown className={cn('size-3 transition-transform duration-(--duration-fast)', raw && 'rotate-180')} aria-hidden />
          </Pressable>
          {(raw || facts.length === 0) && (
            <pre className="motion-arrive max-h-72 overflow-auto rounded-card bg-sunken px-4 py-3 font-mono text-caption leading-relaxed text-fg whitespace-pre-wrap break-all">{stepInputJson(p.step.input)}</pre>
          )}
        </div>
        <Note label="Why it asks">{stepWhy(p.step)}</Note>
        {s.canAnswer ? (
          <Input label="Note for the assistant" placeholder="Optional" value={s.note} onChange={(e) => s.setNote(e.target.value)} maxLength={2000} />
        ) : (
          <p className="text-ui text-fg-muted">{tierBlockedText(p.step)}</p>
        )}
        <ErrorLine text={s.error} />
      </div>
    ),
    footer: (
      <div className="flex w-full items-center justify-end gap-2">
        <Button size="sm" variant="secondary" icon={<Ban />} loading={s.busy === 'deny'} disabled={!s.canAnswer || !!s.busy} onClick={() => void answer('deny')}>
          Deny step
        </Button>
        <Button size="sm" variant="primary" icon={<Check />} loading={s.busy === 'allow'} disabled={!s.canAnswer || !!s.busy} onClick={() => void answer('allow')}>
          Approve step
        </Button>
      </div>
    ),
  });
}

// ---------------------------------------------------------------------------
// Question, plan, MCP form (answered in place, R-HL-6)
// ---------------------------------------------------------------------------

function AskDetail({ item, ctx, now, frame }: { item: Extract<InboxItem, { kind: 'ask' }>; ctx: DetailContext; now: number; frame: Frame }) {
  const a = item.approval;
  const sum = askSummary(a);
  const title = a.ask?.type === 'plan' ? planTitle(a.ask.plan) : askHeading(a);
  return (
    <AskAnswer
      approval={a}
      surface="inbox"
      keys
      render={(p) =>
        frame({
          status: (
            <>
              <StatusPill status="needs-you" />
              <TimeLeft a={a} now={now} />
            </>
          ),
          title,
          facts: (
            <FactGrid
              rows={[
                whereFacts(ctx),
                [
                  { label: 'Machine', value: ctx.machine },
                  { label: 'Server', value: sum.server, mono: true },
                  { label: 'Asked', value: clock(a.createdAt) },
                ],
              ]}
            />
          ),
          tools: <Tools sessionId={a.sessionId} link={`/inbox?approval=${encodeURIComponent(a.id)}`} />,
          body: (
            <div className="flex flex-col gap-6">
              {p.body}
              {p.footer && <p className="text-caption text-fg-subtle num">{fallbackLine(a, 'inbox', formatClock(a.expiresAt))}</p>}
            </div>
          ),
          footer: p.footer ?? undefined,
        })
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Question
// ---------------------------------------------------------------------------

/** A session waiting in its own conversation: blocked (Needs you), or asked, when its turn ended on a question (ADR 0056). */
function QuestionDetail({ item, ctx, title, frame }: { item: Extract<InboxItem, { kind: 'question' | 'asked' }>; ctx: DetailContext; title: string; frame: Frame }) {
  const s = item.session;
  const [, openSession] = useSessionParam();
  const text = plainText(sessionReason(s));
  // The whole question (ADR 0056): the message above it, quiet, then the question itself.
  const v = askViewOf(s, false);
  return frame({
    status: item.kind === 'asked' ? <StatusPill status="idle" label="Asked" /> : <StatusPill status="needs-you" />,
    title,
    heading: (
      // Two lines before it is cut: on a phone one line held only a few words of the title.
      <span className="flex min-w-0 items-start gap-2">
        <AgentGlyph kind={s.agentKind} className="mt-1 size-4 shrink-0" />
        <span className="line-clamp-2 min-w-0">{title}</span>
      </span>
    ),
    facts: (
      <FactGrid
        rows={[
          [
            { label: 'Project', value: ctx.project ? <ProjectValue projectId={s.projectId} name={ctx.project} /> : undefined, title: ctx.project },
            { label: 'Machine', value: ctx.machine },
            { label: 'Asked', value: clock(item.at) },
          ],
        ]}
      />
    ),
    tools: <Tools sessionId={s.id} link={`/sessions?session=${encodeURIComponent(s.id)}`} />,
    body: (
      <div className="flex flex-col gap-6">
        {v.body || v.question ? (
          <div className="flex flex-col gap-3 rounded-card bg-hover px-4 py-3">
            {v.body && <Markdown source={v.body} size="sm" className="text-fg-muted" />}
            {v.question && <Markdown source={v.question} className="font-medium text-fg" />}
          </div>
        ) : (
          <blockquote className="whitespace-pre-wrap rounded-card bg-hover px-4 py-3 text-body text-fg">{text || 'Claude is waiting for your answer.'}</blockquote>
        )}
      </div>
    ),
    footer: (
      <div className="flex w-full items-center justify-end gap-2">
        {item.kind === 'asked' && <AskActions ask={{ askId: askIdOf(s), sessionId: s.id, reason: text }} className="mr-auto" align="start" />}
        <Button size="sm" variant="primary" icon={<SquareTerminal />} onClick={() => openSession(s.id)}>
          Answer in session
        </Button>
      </div>
    ),
  });
}

// ---------------------------------------------------------------------------
// Dismissed
// ---------------------------------------------------------------------------

/** A dismissed or snoozed question: what it asked, who set it aside and when, and Undo. */
function DismissedDetail({ item, title, frame }: { item: Extract<InboxItem, { kind: 'dismissed' }>; title: string; frame: Frame }) {
  const x = item.record;
  const [, openSession] = useSessionParam();
  return frame({
    status: <StatusPill status="ended" label={x.kind === 'dismiss' ? 'Dismissed' : 'Snoozed'} />,
    title: title || 'A session that ended',
    context: '',
    tools: <Tools sessionId={x.sessionId} link={`/sessions?session=${encodeURIComponent(x.sessionId)}`} />,
    body: (
      <div className="flex flex-col gap-6">
        {x.question && <blockquote className="whitespace-pre-wrap rounded-card bg-hover px-4 py-3 text-body text-fg">{x.question}</blockquote>}
        <SetAsideLine record={x} />
        <FactGrid rows={[[{ label: 'From', value: x.from }]]} />
      </div>
    ),
    footer: (
      <div className="flex w-full items-center justify-end gap-2">
        <Button size="sm" variant="secondary" icon={<SquareTerminal />} onClick={() => openSession(x.sessionId)}>
          Open session
        </Button>
      </div>
    ),
  });
}

// ---------------------------------------------------------------------------
// Done
// ---------------------------------------------------------------------------

function DoneDetail({ a, ctx, frame, stillBlocks, missionId }: { a: ApprovalRequest; ctx: DetailContext; frame: Frame; stillBlocks: boolean; missionId: string | null }) {
  const [, openSession] = useSessionParam();
  const navigate = useNavigate();
  const who = useAnswerer();
  const by = decidedText(a, who);
  const sa = a.sessionAnswer;
  // A question, plan or form: what was asked, then what the person answered (R-HL-6), in Orgus
  // or in the session after Orgus passed it back (ADR 0056). Each question shows its own answer.
  const ask = isAnswerable(a) ? askOf(a) : null;
  const perQuestion = !!ask && ask.type === 'questions' && !!questionAnswersOf(a);
  const decided = !!sa || (a.state !== 'expired' && !a.handedBack);
  const answered = ask && decided && !perQuestion ? `${answerLine(ask, givenOf(a), sa ? (sa.by ? who(sa.by) : 'The session') : who(a.decidedBy))}${sa ? ' in the session' : ''}` : null;
  const kept = !!sa && sa.outcome === 'answered' && ask?.type === 'questions' && !perQuestion;
  return frame({
    status: <StatusPill status={a.state === 'allowed' || sa?.outcome === 'answered' ? 'done' : 'ended'} label={doneLabel(a)} />,
    title: ask ? askSummary(a).title : a.title,
    heading: ask ? undefined : <RequestHeading a={a} />,
    facts: (
      <FactGrid
        rows={[
          whereFacts(ctx),
          [
            { label: sa ? 'Answered' : 'Decided', value: by, title: by },
            { label: 'When', value: sa ? clock(sa.at) : a.decidedAt ? clock(a.decidedAt) : undefined },
            { label: 'Asked', value: clock(a.createdAt) },
            { label: 'Files sent', value: a.attachmentIds?.length ? String(a.attachmentIds.length) : undefined },
            { label: 'Policy', value: a.delegate ? delegatedPolicyText(a.delegate) : undefined, title: a.delegate ? delegatedNote(a.delegate) : undefined },
          ],
        ]}
      />
    ),
    tools: <Tools sessionId={a.sessionId} link={`/inbox?approval=${encodeURIComponent(a.id)}`} />,
    body: (
      <div className="flex flex-col gap-6">
        {ask ? <AskRecord approval={a} /> : <CommandBlock approval={a} />}
        {answered && <p className="rounded-card bg-hover px-4 py-3 text-ui text-fg">{answered}</p>}
        {kept && <p className="text-caption text-fg-subtle">The answers stay on the machine: this workspace does not send transcript content.</p>}
        {!ask && a.note && a.state !== 'expired' && <Note label="Note">{a.note}</Note>}
      </div>
    ),
    footer: (
      <div className="flex w-full items-center justify-end gap-2">
        <Button size="sm" variant="secondary" icon={<SquareTerminal />} onClick={() => openSession(a.sessionId)}>
          Open session
        </Button>
        {a.parked && missionId && (
          <Button size="sm" variant={stillBlocks && approvalResumable(a) ? 'secondary' : 'primary'} icon={<RotateCcw />} onClick={() => navigate(`/missions/${encodeURIComponent(missionId)}?retry=1`)}>
            Retry mission
          </Button>
        )}
        {a.parked && stillBlocks && approvalResumable(a) && <ApproveAndResumeButton approvalId={a.id} />}
      </div>
    ),
  });
}

// ---------------------------------------------------------------------------
// The switch
// ---------------------------------------------------------------------------

export interface InboxDetailProps {
  item: InboxItem;
  ctx: DetailContext;
  /** The title a question shows (its session's). */
  title: string;
  now: number;
  request: RequestHandlers;
  stillBlocks: boolean;
  missionId: string | null;
  onStepDone: () => void;
  frame: Frame;
}

export function InboxDetail({ item, ctx, title, now, request, stillBlocks, missionId, onStepDone, frame }: InboxDetailProps) {
  switch (item.kind) {
    case 'request':
      return <RequestDetail key={item.key} a={item.approval} ctx={ctx} now={now} h={request} frame={frame} />;
    case 'ask':
      return <AskDetail key={item.key} item={item} ctx={ctx} now={now} frame={frame} />;
    case 'blocked':
      return <BlockedDetail key={item.key} item={item} ctx={ctx} frame={frame} />;
    case 'step':
      return <StepDetail key={item.key} item={item} frame={frame} onDone={onStepDone} />;
    case 'question':
    case 'asked':
      return <QuestionDetail key={item.key} item={item} ctx={ctx} title={title} frame={frame} />;
    case 'dismissed':
      return <DismissedDetail key={item.key} item={item} title={title} frame={frame} />;
    case 'done':
      return <DoneDetail key={item.key} a={item.approval} ctx={ctx} frame={frame} stillBlocks={stillBlocks} missionId={missionId} />;
  }
}
